import { useState, useEffect, useRef } from 'react';
import { vendorsAPI } from '../../api';
import toast from 'react-hot-toast';

/* ── helpers ─────────────────────────────────────────────────── */
const fmt  = n => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';
const val  = v => v || '—';

function Stars({ rating }) {
  if (!rating) return <span style={{ color:'#94a3b8', fontSize:12 }}>Not rated</span>;
  return (
    <span style={{ display:'flex', alignItems:'center', gap:2 }}>
      {[1,2,3,4,5].map(s => (
        <span key={s} style={{ fontSize:14,
          color: s <= Math.round(rating) ? '#f59e0b' : '#e2e8f0' }}>★</span>
      ))}
      <span style={{ fontSize:11, color:'#64748b', marginLeft:4 }}>{rating}/5</span>
    </span>
  );
}

/* ── Field row for detail panel ─────────────────────────────── */
function DR({ label, children }) {
  if (!children || children === '—') return null;
  return (
    <div style={{ display:'flex', gap:8, padding:'7px 0',
      borderBottom:'1px solid #f1f5f9' }}>
      <span style={{ width:140, flexShrink:0, fontSize:12,
        color:'#94a3b8', fontWeight:500 }}>{label}</span>
      <span style={{ fontSize:13, color:'#1a1f2e', fontWeight:500,
        flex:1, wordBreak:'break-word' }}>{children}</span>
    </div>
  );
}

/* ── Input helper for forms ──────────────────────────────────── */
function Field({ label, name, value, onChange, type='text',
  required, placeholder, options, textarea, half }) {
  const base = {
    width:'100%', border:'1px solid #e2e8f0', borderRadius:8,
    padding:'8px 11px', fontSize:13, color:'#1a1f2e',
    background:'#fff', outline:'none', boxSizing:'border-box',
    fontFamily:'inherit',
  };
  return (
    <div style={{ gridColumn: half ? 'span 1' : 'span 2' }}>
      <label style={{ display:'block', fontSize:12, fontWeight:600,
        color:'#374151', marginBottom:4 }}>
        {label}{required && <span style={{ color:'#dc2626' }}> *</span>}
      </label>
      {options ? (
        <select name={name} value={value||''} onChange={onChange} style={base}>
          <option value="">Select…</option>
          {options.map(o => <option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
        </select>
      ) : textarea ? (
        <textarea name={name} value={value||''} onChange={onChange}
          rows={3} placeholder={placeholder} style={{ ...base, resize:'vertical' }} />
      ) : (
        <input name={name} type={type} value={value||''} onChange={onChange}
          placeholder={placeholder} required={required} style={base} />
      )}
    </div>
  );
}

/* ── Section header inside modal ────────────────────────────── */
function Sec({ children }) {
  return (
    <div style={{ gridColumn:'span 2', paddingTop:12,
      borderTop:'1px solid #f1f5f9', marginTop:4 }}>
      <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8',
        textTransform:'uppercase', letterSpacing:'.07em' }}>{children}</div>
    </div>
  );
}

/* ── VENDOR FORM MODAL ───────────────────────────────────────── */
const EMPTY = {
  vendor_code:'', name:'', contact_person:'', designation:'', email:'',
  mobile:'', phone:'', billing_address:'', shipping_address:'',
  gst_no:'', gst_type:'', pan_number:'', website:'', support_helpline:'',
  support_email:'', payment_mode:'', bank_country:'', bank_name:'',
  account_number:'', ifsc_code:'', vendor_rating:'', is_preferred:false, is_active:true,
};

function VendorModal({ vendor, onClose, onSaved }) {
  const [form, setForm] = useState(vendor
    ? { ...EMPTY, ...vendor, vendor_rating: vendor.vendor_rating ?? '' }
    : { ...EMPTY }
  );
  const [saving, setSaving] = useState(false);

  const set = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const submit = async e => {
    e.preventDefault();
    if (!form.vendor_code.trim()) return toast.error('Vendor code is required');
    if (!form.name.trim())        return toast.error('Vendor name is required');
    setSaving(true);
    try {
      const payload = { ...form,
        vendor_rating: form.vendor_rating ? parseFloat(form.vendor_rating) : null };
      if (vendor?.id) {
        await vendorsAPI.update(vendor.id, payload);
        toast.success('Vendor updated!');
      } else {
        await vendorsAPI.create(payload);
        toast.success('Vendor created!');
      }
      onSaved();
    } catch(err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,14,26,.55)',
      backdropFilter:'blur(4px)', zIndex:1300, display:'flex',
      alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'#fff', borderRadius:14, width:700, maxWidth:'96vw',
        maxHeight:'92vh', display:'flex', flexDirection:'column',
        boxShadow:'0 24px 64px rgba(0,0,0,.22)', overflow:'hidden' }}>

        {/* header */}
        <div style={{ padding:'16px 22px', borderBottom:'1px solid #e2e8f0',
          display:'flex', alignItems:'center', justifyContent:'space-between',
          flexShrink:0 }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:'#1a1f2e' }}>
              {vendor?.id ? 'Edit Vendor' : 'Add New Vendor'}
            </div>
            <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>
              Fill in the vendor details below
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none',
            fontSize:20, cursor:'pointer', color:'#94a3b8', lineHeight:1 }}>✕</button>
        </div>

        {/* body */}
        <div style={{ flex:1, overflowY:'auto', padding:'20px 22px' }}>
          <form id="vform" onSubmit={submit}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>

              <Sec>Basic Information</Sec>
              <Field label="Vendor Code" name="vendor_code" value={form.vendor_code}
                onChange={set} required placeholder="e.g. VND-0001" half />
              <Field label="Vendor Name" name="name" value={form.name}
                onChange={set} required placeholder="Full vendor name" half />

              <Sec>Contact Details</Sec>
              <Field label="Contact Person" name="contact_person" value={form.contact_person}
                onChange={set} placeholder="Mr. Vivek Kulkarni" half />
              <Field label="Designation" name="designation" value={form.designation}
                onChange={set} placeholder="Regional Sales Manager" half />
              <Field label="Email" name="email" type="email" value={form.email}
                onChange={set} placeholder="vendor@company.com" half />
              <Field label="Mobile" name="mobile" value={form.mobile}
                onChange={set} placeholder="+91 98765 43210" half />
              <Field label="Phone / Landline" name="phone" value={form.phone}
                onChange={set} placeholder="022-12345678" half />
              <Field label="Website" name="website" value={form.website}
                onChange={set} placeholder="www.vendor.com" half />
              <Field label="Support Helpline" name="support_helpline" value={form.support_helpline}
                onChange={set} placeholder="1800-xxx-xxxx" half />
              <Field label="Support Email" name="support_email" type="email" value={form.support_email}
                onChange={set} placeholder="support@vendor.com" half />

              <Sec>Billing & Shipping Address</Sec>
              <Field label="Billing Address" name="billing_address" value={form.billing_address}
                onChange={set} textarea placeholder="Full billing address…" />
              <Field label="Shipping Address" name="shipping_address" value={form.shipping_address}
                onChange={set} textarea placeholder="Full shipping address (if different)…" />

              <Sec>Tax & Compliance</Sec>
              <Field label="GST Number" name="gst_no" value={form.gst_no}
                onChange={set} placeholder="27AAECM4162K1Z5" half />
              <Field label="GST Type" name="gst_type" value={form.gst_type}
                onChange={set} placeholder="Regular / Composition / Unregistered" half
                options={['Regular','Composition','Unregistered','SEZ']} />
              <Field label="PAN Number" name="pan_number" value={form.pan_number}
                onChange={set} placeholder="AAECM4162K" half />

              <Sec>Payment & Banking</Sec>
              <Field label="Payment Mode" name="payment_mode" value={form.payment_mode}
                onChange={set} half
                options={['RTGS','NEFT','IMPS','Cheque','DD','Cash','UPI']} />
              <Field label="Bank Name" name="bank_name" value={form.bank_name}
                onChange={set} placeholder="HDFC Bank" half />
              <Field label="Account Number" name="account_number" value={form.account_number}
                onChange={set} placeholder="Account number" half />
              <Field label="IFSC Code" name="ifsc_code" value={form.ifsc_code}
                onChange={set} placeholder="HDFC0001234" half />
              <Field label="Bank Country" name="bank_country" value={form.bank_country}
                onChange={set} placeholder="India" half />

              <Sec>Rating & Status</Sec>
              <Field label="Vendor Rating (1-5)" name="vendor_rating" type="number"
                value={form.vendor_rating} onChange={set}
                placeholder="4.5" half />
              <div style={{ display:'flex', gap:20, alignItems:'center', paddingTop:8 }}>
                <label style={{ display:'flex', alignItems:'center', gap:8,
                  fontSize:13, cursor:'pointer', color:'#374151', fontWeight:500 }}>
                  <input type="checkbox" name="is_preferred"
                    checked={!!form.is_preferred} onChange={set}
                    style={{ width:16, height:16, cursor:'pointer' }} />
                  Preferred Vendor
                </label>
                <label style={{ display:'flex', alignItems:'center', gap:8,
                  fontSize:13, cursor:'pointer', color:'#374151', fontWeight:500 }}>
                  <input type="checkbox" name="is_active"
                    checked={!!form.is_active} onChange={set}
                    style={{ width:16, height:16, cursor:'pointer' }} />
                  Active
                </label>
              </div>
            </div>
          </form>
        </div>

        {/* footer */}
        <div style={{ padding:'14px 22px', borderTop:'1px solid #e2e8f0',
          display:'flex', justifyContent:'flex-end', gap:10, flexShrink:0 }}>
          <button onClick={onClose}
            style={{ padding:'8px 20px', borderRadius:8, border:'1px solid #e2e8f0',
              background:'#fff', fontSize:13, cursor:'pointer', color:'#374151' }}>
            Cancel
          </button>
          <button form="vform" type="submit" disabled={saving}
            style={{ padding:'8px 22px', borderRadius:8, border:'none',
              background: saving ? '#93c5fd' : '#1a56db', color:'#fff',
              fontSize:13, fontWeight:700, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Saving…' : vendor?.id ? 'Update Vendor' : 'Create Vendor'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── DETAIL DRAWER ───────────────────────────────────────────── */
function VendorDrawer({ vendor, onClose, onEdit, onDelete }) {
  if (!vendor) return null;
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,14,26,.45)',
      backdropFilter:'blur(3px)', zIndex:1200, display:'flex',
      justifyContent:'flex-end' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width:440, maxWidth:'96vw', height:'100%', background:'#fff',
        boxShadow:'-8px 0 40px rgba(0,0,0,.14)', display:'flex',
        flexDirection:'column', overflow:'hidden',
        animation:'slideIn .25s ease' }}>

        {/* drawer header */}
        <div style={{ padding:'18px 20px 14px', borderBottom:'1px solid #e2e8f0',
          flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'flex-start',
            justifyContent:'space-between' }}>
            <div style={{ display:'flex', gap:12, alignItems:'center' }}>
              <div style={{ width:46, height:46, borderRadius:12, background:'#f0f6ff',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:22, flexShrink:0 }}>🏭</div>
              <div>
                <div style={{ fontSize:16, fontWeight:800, color:'#1a1f2e',
                  lineHeight:1.2 }}>{vendor.name}</div>
                <div style={{ fontSize:12, color:'#94a3b8', marginTop:3,
                  fontFamily:'monospace' }}>{vendor.vendor_code}</div>
                <div style={{ display:'flex', gap:6, marginTop:5 }}>
                  {vendor.is_preferred && (
                    <span style={{ fontSize:10, fontWeight:700, color:'#16a34a',
                      background:'#dcfce7', padding:'2px 8px', borderRadius:20 }}>
                      ★ Preferred
                    </span>
                  )}
                  <span style={{ fontSize:10, fontWeight:700,
                    color: vendor.is_active ? '#1a56db' : '#dc2626',
                    background: vendor.is_active ? '#dbeafe' : '#fee2e2',
                    padding:'2px 8px', borderRadius:20 }}>
                    {vendor.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} style={{ background:'none', border:'none',
              fontSize:20, cursor:'pointer', color:'#94a3b8' }}>✕</button>
          </div>
          {/* action buttons */}
          <div style={{ display:'flex', gap:8, marginTop:14 }}>
            <button onClick={onEdit}
              style={{ flex:1, padding:'7px', borderRadius:8,
                border:'1px solid #1a56db', background:'#dbeafe',
                color:'#1a56db', fontSize:12, fontWeight:700, cursor:'pointer' }}>
              ✏ Edit
            </button>
            <button onClick={onDelete}
              style={{ flex:1, padding:'7px', borderRadius:8,
                border:'1px solid #fee2e2', background:'#fff',
                color:'#dc2626', fontSize:12, fontWeight:700, cursor:'pointer' }}>
              🗑 Deactivate
            </button>
          </div>
        </div>

        {/* drawer body */}
        <div style={{ flex:1, overflowY:'auto', padding:'14px 20px' }}>

          {vendor.vendor_rating && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8',
                textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 }}>
                Rating
              </div>
              <Stars rating={vendor.vendor_rating} />
            </div>
          )}

          {/* Contact */}
          <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8',
            textTransform:'uppercase', letterSpacing:'.06em',
            margin:'10px 0 6px' }}>Contact</div>
          <DR label="Contact Person">{val(vendor.contact_person)}</DR>
          <DR label="Designation">{val(vendor.designation)}</DR>
          <DR label="Mobile">{val(vendor.mobile)}</DR>
          <DR label="Phone">{val(vendor.phone)}</DR>
          <DR label="Email">
            {vendor.email
              ? <a href={`mailto:${vendor.email}`} style={{ color:'#1a56db' }}>{vendor.email}</a>
              : '—'}
          </DR>
          <DR label="Support Helpline">{val(vendor.support_helpline)}</DR>
          <DR label="Support Email">
            {vendor.support_email
              ? <a href={`mailto:${vendor.support_email}`} style={{ color:'#1a56db' }}>{vendor.support_email}</a>
              : '—'}
          </DR>
          <DR label="Website">
            {vendor.website
              ? <a href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`}
                  target="_blank" rel="noreferrer" style={{ color:'#1a56db' }}>
                  {vendor.website}
                </a>
              : '—'}
          </DR>

          {/* Address */}
          <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8',
            textTransform:'uppercase', letterSpacing:'.06em',
            margin:'14px 0 6px' }}>Address</div>
          <DR label="Billing">{val(vendor.billing_address)}</DR>
          <DR label="Shipping">{val(vendor.shipping_address)}</DR>

          {/* Tax */}
          <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8',
            textTransform:'uppercase', letterSpacing:'.06em',
            margin:'14px 0 6px' }}>Tax & Compliance</div>
          <DR label="GST Number">
            <span style={{ fontFamily:'monospace' }}>{val(vendor.gst_no)}</span>
          </DR>
          <DR label="GST Type">{val(vendor.gst_type)}</DR>
          <DR label="PAN Number">
            <span style={{ fontFamily:'monospace' }}>{val(vendor.pan_number)}</span>
          </DR>

          {/* Banking */}
          <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8',
            textTransform:'uppercase', letterSpacing:'.06em',
            margin:'14px 0 6px' }}>Banking & Payment</div>
          <DR label="Payment Mode">{val(vendor.payment_mode)}</DR>
          <DR label="Bank Name">{val(vendor.bank_name)}</DR>
          <DR label="Account Number">
            <span style={{ fontFamily:'monospace' }}>{val(vendor.account_number)}</span>
          </DR>
          <DR label="IFSC Code">
            <span style={{ fontFamily:'monospace' }}>{val(vendor.ifsc_code)}</span>
          </DR>
          <DR label="Bank Country">{val(vendor.bank_country)}</DR>

          <div style={{ padding:'14px 0 4px', fontSize:11, color:'#94a3b8' }}>
            Created {vendor.created_at ? new Date(vendor.created_at).toLocaleDateString('en-IN') : '—'}
          </div>
        </div>
      </div>
      <style>{`@keyframes slideIn{from{transform:translateX(100%)}to{transform:none}}`}</style>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
export default function VendorPage() {
  const [vendors,  setVendors]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [filter,   setFilter]   = useState('all');   // all | active | preferred
  const [selected, setSelected] = useState(null);    // drawer
  const [modal,    setModal]    = useState(null);     // null | 'add' | vendor_obj

  const searchRef = useRef();
  const debounce  = useRef();

  /* load */
  const load = (q = search, f = filter) => {
    setLoading(true);
    const params = { limit:100 };
    if (q)              params.q         = q;
    if (f === 'active')   params.is_active = true;
    if (f === 'preferred') params.preferred = true;

    vendorsAPI.list(params)
      .then(r => { setVendors(r.data?.data || []); setTotal(r.data?.total || 0); })
      .catch(() => toast.error('Failed to load vendors'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSearch = e => {
    const q = e.target.value;
    setSearch(q);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(q, filter), 350);
  };

  const handleFilter = f => {
    setFilter(f);
    load(search, f);
  };

  const handleSaved = () => {
    setModal(null);
    setSelected(null);
    load();
    setSelected(null);
  };

  const handleDelete = async v => {
    if (!window.confirm(`Deactivate "${v.name}"?`)) return;
    try {
      await vendorsAPI.delete(v.id);
      toast.success('Vendor deactivated');
      setSelected(null);
      load();
    } catch { toast.error('Failed to deactivate'); }
  };

  const handleTogglePreferred = async (v, e) => {
    e.stopPropagation();
    try {
      await vendorsAPI.togglePreferred(v.id);
      load();
    } catch { toast.error('Failed to update'); }
  };

  /* stats */
  const activeCount    = vendors.filter(v => v.is_active).length;
  const preferredCount = vendors.filter(v => v.is_preferred).length;

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%',
      background:'#f8fafc' }}>

      {/* ── Top bar ─────────────────────────────────────────── */}
      <div style={{ padding:'18px 24px 14px', background:'#fff',
        borderBottom:'1px solid #e2e8f0', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center',
          justifyContent:'space-between', marginBottom:14 }}>
          <div>
            <h1 style={{ fontSize:20, fontWeight:800, color:'#1a1f2e', margin:0 }}>
              Vendors
            </h1>
            <div style={{ fontSize:12, color:'#64748b', marginTop:3 }}>
              {total} vendor{total !== 1 ? 's' : ''} · {activeCount} active · {preferredCount} preferred
            </div>
          </div>
          <button onClick={() => setModal('add')}
            style={{ padding:'9px 18px', background:'#1a56db', color:'#fff',
              border:'none', borderRadius:9, fontSize:13, fontWeight:700,
              cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
            + Add Vendor
          </button>
        </div>

        {/* Search + filters */}
        <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:220, maxWidth:380, position:'relative' }}>
            <span style={{ position:'absolute', left:11, top:'50%',
              transform:'translateY(-50%)', color:'#94a3b8', fontSize:14 }}>⌕</span>
            <input ref={searchRef} value={search} onChange={handleSearch}
              placeholder="Search vendors, code, contact…"
              style={{ width:'100%', border:'1px solid #e2e8f0', borderRadius:8,
                padding:'8px 12px 8px 32px', fontSize:13, color:'#1a1f2e',
                background:'#f8fafc', outline:'none', boxSizing:'border-box' }} />
          </div>
          {['all','active','preferred'].map(f => (
            <button key={f} onClick={() => handleFilter(f)}
              style={{ padding:'7px 14px', borderRadius:20, fontSize:12,
                fontWeight: filter===f ? 700 : 500, cursor:'pointer',
                border: filter===f ? 'none' : '1px solid #e2e8f0',
                background: filter===f ? '#1a56db' : '#fff',
                color: filter===f ? '#fff' : '#64748b' }}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table ───────────────────────────────────────────── */}
      <div style={{ flex:1, overflowY:'auto', padding:'0 24px 24px' }}>
        {loading ? (
          <div style={{ display:'flex', flexDirection:'column', gap:8, paddingTop:16 }}>
            {Array(6).fill(0).map((_, i) => (
              <div key={i} style={{ height:64, background:'#fff', borderRadius:10,
                border:'1px solid #e2e8f0',
                background:'linear-gradient(90deg,#f1f5f9 25%,#e8edf2 50%,#f1f5f9 75%)',
                backgroundSize:'200% 100%', animation:'shimmer 1.4s infinite' }} />
            ))}
            <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
          </div>
        ) : vendors.length === 0 ? (
          <div style={{ textAlign:'center', paddingTop:80, color:'#94a3b8' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>🏭</div>
            <div style={{ fontSize:16, fontWeight:700, color:'#1a1f2e', marginBottom:6 }}>
              No vendors found
            </div>
            <div style={{ fontSize:13, marginBottom:20 }}>
              {search ? `No results for "${search}"` : 'Add your first vendor to get started'}
            </div>
            {!search && (
              <button onClick={() => setModal('add')}
                style={{ padding:'9px 20px', background:'#1a56db', color:'#fff',
                  border:'none', borderRadius:8, fontSize:13, fontWeight:600,
                  cursor:'pointer' }}>
                + Add Vendor
              </button>
            )}
          </div>
        ) : (
          <div style={{ marginTop:16, border:'1px solid #e2e8f0',
            borderRadius:12, overflow:'hidden', background:'#fff' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Vendor','Code','Contact','Mobile','GST No','Rating','Status',''].map(h => (
                    <th key={h} style={{ padding:'11px 14px', fontSize:11,
                      fontWeight:700, textTransform:'uppercase',
                      letterSpacing:'.05em', color:'#94a3b8',
                      textAlign:'left', whiteSpace:'nowrap',
                      borderBottom:'1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vendors.map((v, i) => (
                  <tr key={v.id}
                    onClick={() => setSelected(v)}
                    style={{ borderBottom: i < vendors.length-1 ? '1px solid #f1f5f9':'none',
                      cursor:'pointer', transition:'background .1s' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background=''}>

                    {/* Vendor name + preferred */}
                    <td style={{ padding:'13px 14px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:36, height:36, borderRadius:9,
                          background:'#f0f6ff', display:'flex', alignItems:'center',
                          justifyContent:'center', fontSize:16, flexShrink:0 }}>🏭</div>
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, color:'#1a1f2e',
                            display:'flex', alignItems:'center', gap:6 }}>
                            {v.name}
                            {v.is_preferred && (
                              <span style={{ fontSize:9, fontWeight:700,
                                color:'#16a34a', background:'#dcfce7',
                                padding:'1px 6px', borderRadius:20 }}>
                                ★ Preferred
                              </span>
                            )}
                          </div>
                          {v.email && (
                            <div style={{ fontSize:11, color:'#94a3b8', marginTop:1 }}>{v.email}</div>
                          )}
                        </div>
                      </div>
                    </td>

                    <td style={{ padding:'13px 14px' }}>
                      <span style={{ fontFamily:'monospace', fontSize:12,
                        color:'#1a56db', fontWeight:600 }}>{v.vendor_code}</span>
                    </td>

                    <td style={{ padding:'13px 14px', fontSize:13, color:'#374151' }}>
                      {v.contact_person || '—'}
                      {v.designation && (
                        <div style={{ fontSize:11, color:'#94a3b8' }}>{v.designation}</div>
                      )}
                    </td>

                    <td style={{ padding:'13px 14px', fontSize:13, color:'#374151' }}>
                      {v.mobile || v.phone || '—'}
                    </td>

                    <td style={{ padding:'13px 14px' }}>
                      <span style={{ fontFamily:'monospace', fontSize:12, color:'#64748b' }}>
                        {v.gst_no || '—'}
                      </span>
                    </td>

                    <td style={{ padding:'13px 14px' }}>
                      <Stars rating={v.vendor_rating} />
                    </td>

                    <td style={{ padding:'13px 14px' }}>
                      <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11,
                        fontWeight:600,
                        background: v.is_active ? '#dcfce7' : '#f1f5f9',
                        color:      v.is_active ? '#15803d' : '#94a3b8' }}>
                        {v.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>

                    {/* row actions */}
                    <td style={{ padding:'13px 14px' }}>
                      <div style={{ display:'flex', gap:6 }}
                        onClick={e => e.stopPropagation()}>
                        <button onClick={() => setModal(v)}
                          style={{ padding:'4px 10px', fontSize:11, fontWeight:600,
                            border:'1px solid #e2e8f0', borderRadius:6,
                            background:'#fff', cursor:'pointer', color:'#374151' }}>
                          Edit
                        </button>
                        <button onClick={e => handleTogglePreferred(v, e)}
                          title={v.is_preferred ? 'Remove from preferred' : 'Mark preferred'}
                          style={{ padding:'4px 8px', fontSize:13,
                            border:'1px solid #e2e8f0', borderRadius:6,
                            background: v.is_preferred ? '#fef3c7' : '#fff',
                            cursor:'pointer' }}>
                          {v.is_preferred ? '★' : '☆'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Detail Drawer ────────────────────────────────────── */}
      {selected && (
        <VendorDrawer
          vendor={selected}
          onClose={() => setSelected(null)}
          onEdit={() => { setModal(selected); setSelected(null); }}
          onDelete={() => handleDelete(selected)}
        />
      )}

      {/* ── Add/Edit Modal ───────────────────────────────────── */}
      {modal && (
        <VendorModal
          vendor={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
